export interface Country {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
  /** Digit count of the national significant number (no trunk prefix). */
  minDigits: number;
  maxDigits: number;
  placeholder: string;
  /**
   * National trunk prefix dialled before the number at home ("0" in
   * "070 123 45 67") and left out after the dial code (#1059). Countries
   * without one (Denmark, Norway) omit it.
   */
  trunkPrefix?: string;
}

export interface PhoneValidation {
  minDigits: number;
  maxDigits: number;
  errorMessage: string;
}

export const COUNTRIES: Country[] = [
  {
    code: "DK",
    dialCode: "+45",
    flag: "🇩🇰",
    name: "Denmark",
    minDigits: 8,
    maxDigits: 8,
    placeholder: "12 34 56 78",
  },
  {
    code: "DE",
    dialCode: "+49",
    flag: "🇩🇪",
    name: "Germany",
    minDigits: 6,
    maxDigits: 11,
    placeholder: "30 12345678",
    trunkPrefix: "0",
  },
  {
    code: "GB",
    dialCode: "+44",
    flag: "🇬🇧",
    name: "United Kingdom",
    minDigits: 10,
    maxDigits: 10,
    placeholder: "7911 123456",
    trunkPrefix: "0",
  },
  {
    code: "NO",
    dialCode: "+47",
    flag: "🇳🇴",
    name: "Norway",
    minDigits: 8,
    maxDigits: 8,
    placeholder: "12 34 56 78",
  },
  {
    code: "SE",
    dialCode: "+46",
    flag: "🇸🇪",
    name: "Sweden",
    minDigits: 7,
    maxDigits: 9,
    placeholder: "70 123 45 67",
    trunkPrefix: "0",
  },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]; // Denmark

/** The listed country whose dial code starts `value`, if any. */
export function findCountryByDialCode(value: string): Country | undefined {
  return COUNTRIES.find(c => value.startsWith(c.dialCode));
}

/**
 * Drop one leading trunk prefix from a country's local digits, so "0701234567"
 * typed for Sweden becomes "701234567" and is stored as +46701234567, not
 * +460701234567 (#1059). Digits of countries without a trunk prefix are
 * returned unchanged.
 */
export function stripTrunkPrefix(country: Country, localDigits: string): string {
  const { trunkPrefix } = country;
  return trunkPrefix && localDigits.startsWith(trunkPrefix)
    ? localDigits.slice(trunkPrefix.length)
    : localDigits;
}

/** Shown under `PhoneInput` when a trunk 0 was dropped from the number (#1059). */
export const TRUNK_PREFIX_HINT = "Leave out the first 0 — we've removed it for you";

const COUNTRY_DISPLAY_NAMES: Record<string, string> = {
  DK: "Danish",
  DE: "German",
  GB: "UK",
  NO: "Norwegian",
  SE: "Swedish",
};

export function getPhoneValidation(countryCode: string): PhoneValidation {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) {
    return { minDigits: 8, maxDigits: 15, errorMessage: "Invalid phone number" };
  }
  const displayName = COUNTRY_DISPLAY_NAMES[countryCode] ?? country.name;
  const digits =
    country.minDigits === country.maxDigits
      ? `${country.minDigits}`
      : `${country.minDigits}–${country.maxDigits}`;
  const trunkNote = country.trunkPrefix ? ` without the leading ${country.trunkPrefix}` : "";
  return {
    minDigits: country.minDigits,
    maxDigits: country.maxDigits,
    errorMessage: `${displayName} phone numbers must be ${digits} digits${trunkNote}`,
  };
}
